import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QtySyncDebouncer } from '../../helpers/qty-sync-debouncer';
import { CartUpdateEvent, SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

@Component({
    selector: 'app-swiggy-assure-supplier',
    template: ''
})
export class SwiggyAssureSupplierComponent implements OnDestroy {

    @Input() entityId = '';
    @Input() supplierSection: SupplierSection | null = null;

    @Output() cartUpdated = new EventEmitter<CartUpdateEvent>();

    apiCartCount = 0;
    apiCartItems: SupplierProduct[] = [];
    cartQuantityMap = new Map<string, number>();

    private destroy$ = new Subject<void>();
    private qtyDebouncer = new QtySyncDebouncer(600);
    private cartRequestCount = 0;

    get isCartSyncing(): boolean {
        return this.cartRequestCount > 0 || this.qtyDebouncer.hasPending();
    }

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService
    ) { }

    ngOnDestroy(): void {
        this.qtyDebouncer.clearAll();
        this.destroy$.next();
        this.destroy$.complete();
    }

    initialize(): void {
        if (this.entityId) {
            this.fetchCart();
        }
    }

    resetState(): void {
        this.qtyDebouncer.clearAll();
        this.apiCartCount = 0;
        this.apiCartItems = [];
        this.cartQuantityMap.clear();
    }

    addToCart(item: SupplierProduct, supplierName: string, previousQuantity: number): void {
        this.qtyDebouncer.schedule(String(item.sku_id), previousQuantity, (baseline) => {
            this.performCartApi(item, baseline, supplierName, true);
        });
        this.syncCartLocally(String(item.sku_id));
    }

    updateQuantity(item: SupplierProduct, change: number, previousQuantity: number): void {
        this.qtyDebouncer.schedule(String(item.sku_id), previousQuantity, (baseline) => {
            this.performCartApi(item, baseline, 'Assure', false);
        });
        this.syncCartLocally(String(item.sku_id));
    }

    syncQuantity(item: SupplierProduct, previousQuantity: number): void {
        this.syncCartLocally(String(item.sku_id));
        this.performCartApi(item, previousQuantity, 'Assure', false);
    }

    flushPendingQuantitySyncs(): void {
        this.qtyDebouncer.flushAll();
    }

    fetchCart(): void {
        if (!this.entityId) {
            return;
        }
        this.priceDiscoveryService.getSwiggyAssureCart(this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.applyCartResponse(response);
                },
                error: (error) => {
                    console.error('Failed to fetch Swiggy Assure cart:', error);
                }
            });
    }

    clearCart(): void {
        if (!this.entityId) {
            return;
        }
        this.priceDiscoveryService.clearSwiggyAssureCart(this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.apiCartItems = [];
                    this.apiCartCount = 0;
                    this.cartQuantityMap.clear();
                    if (this.supplierSection) {
                        this.supplierSection.items.forEach(item => {
                            item.selectedQuantity = 0;
                        });
                    }
                    this.emitCartUpdate();
                },
                error: (error) => {
                    const errorMessage = error?.error?.message || error?.error?.error || 'Failed to clear cart. Please try again.';
                    this.sharedService.errorAlert(errorMessage);
                }
            });
    }

    private performCartApi(item: SupplierProduct, previousQuantity: number, supplierName: string, isFirstAdd: boolean): void {
        const skuId = String(item.sku_id);
        const quantity = item.selectedQuantity || 0;
        const ids = this.cartIds(item);
        if (!ids.itemId || !ids.productId || !ids.spin) {
            item.selectedQuantity = previousQuantity;
            this.syncCartLocally(skuId);
            this.sharedService.errorAlert('This Assure item is missing cart ids');
            return;
        }
        item.isAddingToCart = true;
        this.cartRequestCount += 1;
        this.priceDiscoveryService.updateSwiggyAssureCart({
            itemId: ids.itemId,
            productId: ids.productId,
            spin: ids.spin,
            quantity
        }, this.entityId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                this.cartRequestCount = Math.max(0, this.cartRequestCount - 1);
                item.isAddingToCart = false;
                if (isFirstAdd && quantity > 0 && previousQuantity === 0) {
                    this.sharedService.successAlert(`Added "${item.item_name}" from ${supplierName} to cart`);
                }
                if (!this.qtyDebouncer.isPending(skuId)) {
                    this.applyCartResponse(response);
                }
            },
            error: (error) => {
                this.cartRequestCount = Math.max(0, this.cartRequestCount - 1);
                item.isAddingToCart = false;
                if (this.qtyDebouncer.isPending(skuId)) {
                    return;
                }
                item.selectedQuantity = previousQuantity;
                this.syncCartLocally(skuId);
                const errorMessage = error?.error?.message || error?.error?.error || 'Failed to update cart. Please try again.';
                item.cartError = errorMessage;
                this.sharedService.errorAlert(errorMessage);
            }
        });
    }

    private findSearchItem(itemId: string, productId: string, spin: string): SupplierProduct | undefined {
        const keys = new Set([itemId, productId, spin].filter(Boolean));
        return (this.supplierSection?.items || []).find((item) =>
            [item.swiggyItemId, item.swiggyProductId, item.swiggySpin, item.sku_id]
                .some((key) => key && keys.has(String(key)))
        );
    }

    private orderLimits(source: any, fallback?: SupplierProduct): { minimumOrderQty?: number; maximumOrderQty?: number; avlOrderQty?: number } {
        const rawMin = source?.minimumOrderQty ?? source?.minimum_order_qty ?? fallback?.minimumOrderQty;
        const rawMax = source?.maximumOrderQty ?? source?.maximum_order_qty ?? fallback?.maximumOrderQty;
        const rawAvl = source?.avlOrderQty ?? source?.avl_order_qty ?? source?.availableOrderQty ?? source?.available_order_qty ?? fallback?.avlOrderQty;
        const parsedAvl = rawAvl !== undefined && rawAvl !== null && rawAvl !== '' && !isNaN(Number(rawAvl))
            ? Number(rawAvl)
            : undefined;
        const parsedMax = rawMax !== undefined && rawMax !== null && rawMax !== '' && !isNaN(Number(rawMax)) && Number(rawMax) > 0 && Number(rawMax) < 2147483647
            ? Number(rawMax)
            : undefined;
        const parsedMin = rawMin !== undefined && rawMin !== null && rawMin !== '' && !isNaN(Number(rawMin)) && Number(rawMin) > 0
            ? Number(rawMin)
            : undefined;
        let maximumOrderQty = parsedMax;
        if (parsedAvl !== undefined) {
            maximumOrderQty = maximumOrderQty !== undefined ? Math.min(maximumOrderQty, parsedAvl) : parsedAvl;
        }
        return {
            minimumOrderQty: parsedMin,
            maximumOrderQty,
            avlOrderQty: parsedAvl
        };
    }

    private cartIds(item: SupplierProduct): { itemId: string; productId: string; spin: string } {
        const itemId = String(item.swiggyItemId || item.sku_id || '').trim();
        const productId = String(item.swiggyProductId || item.product_number || itemId).trim();
        const spin = String(item.swiggySpin || itemId).trim();
        return { itemId, productId, spin };
    }

    private applyCartResponse(response: any): void {
        const rows = response?.items || response?.cartItems || [];
        const products: SupplierProduct[] = (Array.isArray(rows) ? rows : []).map((row: any) => {
            const itemId = String(row.itemId || row.item_id || '').trim();
            const productId = String(row.productId || row.product_id || itemId).trim();
            const spin = String(row.spin || itemId).trim();
            const qty = Number(row.quantity || row.qty || 0);
            const rate = Number(row.rate || row.discountedPricePerUnit || row.discounted_price_per_unit || row.price || 0);
            const mrp = Number(row.mrp || row.pricePerUnit || row.price_per_unit || rate);
            const searchItem = this.findSearchItem(itemId, productId, spin);
            const limits = this.orderLimits(row, searchItem);
            return {
                item_name: row.name || row.itemName || row.item_name || itemId,
                sku_id: itemId,
                rate,
                baseRate: rate,
                mrp,
                price: rate,
                unit: row.unit || 'unit',
                quantity: qty,
                platform: 'swiggy',
                isBestMatch: false,
                is_in_stock: true,
                selectedQuantity: qty,
                image_url: row.image || row.imageUrl || row.image_url || '',
                swiggyItemId: itemId,
                swiggyProductId: productId,
                swiggySpin: spin,
                minimumOrderQty: limits.minimumOrderQty,
                maximumOrderQty: limits.maximumOrderQty,
                avlOrderQty: limits.avlOrderQty
            } as SupplierProduct;
        }).filter(item => item.sku_id && (item.selectedQuantity || 0) > 0);

        this.apiCartItems = products;
        this.apiCartCount = products.length;
        this.cartQuantityMap.clear();
        products.forEach(item => {
            const qty = item.selectedQuantity || 0;
            [item.swiggyItemId, item.swiggyProductId, item.swiggySpin, item.sku_id].forEach(key => {
                if (key) {
                    this.cartQuantityMap.set(String(key), qty);
                }
            });
        });
        this.syncSearchQuantities();
        this.emitCartUpdate();
    }

    private syncSearchQuantities(): void {
        const searchItems = this.supplierSection?.items || [];
        searchItems.forEach(item => {
            const keys = [item.swiggyItemId, item.swiggyProductId, item.swiggySpin, item.sku_id]
                .filter(Boolean)
                .map(key => String(key));
            const cartItem = this.apiCartItems.find(row =>
                [row.swiggyItemId, row.swiggyProductId, row.swiggySpin, row.sku_id]
                    .some(key => key && keys.includes(String(key)))
            );
            const qty = cartItem?.selectedQuantity
                || this.cartQuantityMap.get(String(item.swiggyItemId || item.sku_id))
                || this.cartQuantityMap.get(String(item.sku_id))
                || 0;
            item.selectedQuantity = qty;
            if (!cartItem) {
                return;
            }
            if (cartItem.maximumOrderQty) {
                item.maximumOrderQty = cartItem.maximumOrderQty;
            }
            if (cartItem.minimumOrderQty) {
                item.minimumOrderQty = cartItem.minimumOrderQty;
            }
            if (cartItem.avlOrderQty !== undefined) {
                item.avlOrderQty = cartItem.avlOrderQty;
            }
        });
    }

    syncCartLocally(modifiedSkuId?: string): void {
        const searchItems = this.supplierSection?.items || [];
        const activeSkuIds = new Set<string>();
        searchItems.forEach(item => {
            if (item.selectedQuantity && item.selectedQuantity > 0) {
                activeSkuIds.add(String(item.sku_id));
                if (item.swiggyItemId) {
                    activeSkuIds.add(String(item.swiggyItemId));
                }
            }
        });
        if (modifiedSkuId) {
            activeSkuIds.add(String(modifiedSkuId));
        }
        const unchangedApiItems = this.apiCartItems.filter(item => !activeSkuIds.has(String(item.sku_id)));
        const searchItemsInCart = searchItems.filter(item => item.selectedQuantity && item.selectedQuantity > 0);
        this.apiCartItems = [
            ...unchangedApiItems,
            ...searchItemsInCart.map(item => {
                const existing = this.apiCartItems.find(cartItem =>
                    String(cartItem.sku_id) === String(item.swiggyItemId || item.sku_id)
                    || String(cartItem.swiggyItemId || '') === String(item.swiggyItemId || item.sku_id)
                );
                const limits = this.orderLimits(existing || {}, item);
                return {
                    ...item,
                    platform: 'swiggy',
                    selectedQuantity: item.selectedQuantity || 1,
                    quantity: item.selectedQuantity || 1,
                    minimumOrderQty: limits.minimumOrderQty,
                    maximumOrderQty: limits.maximumOrderQty,
                    avlOrderQty: limits.avlOrderQty
                };
            })
        ];
        this.apiCartCount = this.apiCartItems.length;
        this.emitCartUpdate();
    }

    private emitCartUpdate(): void {
        this.cartUpdated.emit({
            platform: 'swiggy',
            cartCount: this.apiCartCount,
            cartItems: this.apiCartItems
        });
    }
}
